// Builds the HTML used by WeasyPrint to render the PDF.
// Kept in pure JS for portability.

import { toImageDataUrl } from "./image";

const fmtDate = (d) => {
  try {
    const date = new Date(d);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const opLabel = {
  DEPOT: "Bordereau de dépôt",
  RESTITUTION: "Bordereau de restitution",
  DELIVRANCE: "Bordereau de délivrance",
};

const escapeHtml = (str = "") =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export function buildOperationHtml({
  operation,
  client,
  photos = [],
  signature = null,
  branding = {},
  utilisateur = "",
}) {
  const title = opLabel[operation.type] || operation.type;
  const photoCells = photos
    .map(
      (src) =>
        `<div class="photo"><img src="${src}" /></div>`
    )
    .join("");

  // The header MUST display the auction house identity (logo + name + tagline).
  // Never fall back to the generic "VIDOC" string — if no branding is set, we
  // show only the auction house fields that are actually configured.
  const logoSrc = toImageDataUrl(branding.icon_base64);
  const logoTag = logoSrc
    ? `<img class="logo" src="${logoSrc}" alt="" />`
    : "";

  const companyName = branding.company_name && String(branding.company_name).trim();
  const companyTagline = branding.company_tagline && String(branding.company_tagline).trim();

  const signatureTag = signature
    ? `<img class="signature" src="${signature}" />`
    : `<div class="signature-placeholder">—</div>`;

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8" />
<style>
  @page { size: A4; margin: 18mm 16mm; }
  body { font-family: -apple-system, "Helvetica Neue", Arial, sans-serif; color: #000; font-size: 11pt; }
  .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #007AFF; padding-bottom: 14px; margin-bottom: 20px; }
  .header .brand { display: flex; align-items: center; gap: 16px; }
  .logo { width: 96px; height: 96px; object-fit: contain; }
  .brand-text { line-height: 1.25; }
  .company { font-size: 20pt; font-weight: 700; letter-spacing: 0.2px; color: #000; }
  .tagline { font-size: 10pt; color: #6E6E73; margin-top: 2px; }
  .doc-info { text-align: right; font-size: 9pt; color: #6E6E73; }
  .doc-info .ref { font-size: 12pt; color: #000; font-weight: 600; }
  h1 { font-size: 18pt; margin: 0 0 16px 0; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 24px; margin-bottom: 16px; }
  .box { border: 1px solid #D1D1D6; border-radius: 8px; padding: 12px; }
  .box h2 { font-size: 11pt; margin: 0 0 8px 0; color: #007AFF; text-transform: uppercase; letter-spacing: 0.5px; }
  .row { display: flex; justify-content: space-between; padding: 3px 0; font-size: 10pt; }
  .row .lbl { color: #6E6E73; }
  .obs { white-space: pre-wrap; }
  .photos { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; margin-top: 8px; }
  .photo { border: 1px solid #D1D1D6; border-radius: 8px; overflow: hidden; height: 240px; }
  .photo img { width: 100%; height: 100%; object-fit: cover; }
  .signature-block { margin-top: 24px; border-top: 1px solid #D1D1D6; padding-top: 16px; display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
  .signature { max-width: 100%; max-height: 100px; }
  .signature-placeholder { color: #C7C7CC; font-size: 24pt; text-align: center; }
  .signature-label { font-size: 9pt; color: #6E6E73; margin-top: 6px; }
  footer { margin-top: 30px; font-size: 8pt; color: #8E8E93; text-align: center; }
</style>
</head>
<body>
  <div class="header">
    <div class="brand">
      ${logoTag}
      <div class="brand-text">
        ${companyName ? `<div class="company">${escapeHtml(companyName)}</div>` : ""}
        ${companyTagline ? `<div class="tagline">${escapeHtml(companyTagline)}</div>` : ""}
      </div>
    </div>
    <div class="doc-info">
      <div class="ref">N° ${escapeHtml(operation.id)}</div>
      <div>${fmtDate(operation.date)}</div>
    </div>
  </div>

  <h1>${title}</h1>

  <div class="grid">
    <div class="box">
      <h2>Client</h2>
      <div class="row"><span class="lbl">Nom</span><span><strong>${escapeHtml([client?.nom, client?.prenom].filter(Boolean).join(" "))}</strong></span></div>
      ${client?.numero_client ? `<div class="row"><span class="lbl">N° client</span><span>${escapeHtml(client.numero_client)}</span></div>` : ""}
      ${client?.email ? `<div class="row"><span class="lbl">Email</span><span>${escapeHtml(client.email)}</span></div>` : ""}
      ${client?.telephone ? `<div class="row"><span class="lbl">Téléphone</span><span>${escapeHtml(client.telephone)}</span></div>` : ""}
      ${client?.adresse1 ? `<div class="row"><span class="lbl">Adresse</span><span>${escapeHtml(client.adresse1)}</span></div>` : ""}
      ${client?.code_postal || client?.ville ? `<div class="row"><span class="lbl">Ville</span><span>${escapeHtml([client.code_postal, client.ville].filter(Boolean).join(" "))}</span></div>` : ""}
    </div>
    <div class="box">
      <h2>Opération</h2>
      <div class="row"><span class="lbl">Type</span><span><strong>${title}</strong></span></div>
      <div class="row"><span class="lbl">Date</span><span>${fmtDate(operation.date)}</span></div>
      <div class="row"><span class="lbl">Utilisateur</span><span>${escapeHtml(utilisateur || operation.utilisateur || "")}</span></div>
      ${operation.signatory_name ? `<div class="row"><span class="lbl">Signataire</span><span>${escapeHtml(operation.signatory_name)}</span></div>` : ""}
      <div class="row"><span class="lbl">Médias</span><span>${photos.length} photo(s)</span></div>
    </div>
  </div>

  ${operation.observations ? `
  <div class="box" style="margin-bottom:16px">
    <h2>Observations</h2>
    <div class="obs">${escapeHtml(operation.observations)}</div>
  </div>` : ""}

  ${photos.length ? `<div class="photos">${photoCells}</div>` : ""}

  <div class="signature-block">
    <div>
      <div class="signature-label">Signature du client</div>
      ${signatureTag}
    </div>
    <div>
      <div class="signature-label">${escapeHtml(companyName || "Maison de ventes")}</div>
      <div style="height:80px"></div>
    </div>
  </div>

  <footer>Document généré le ${fmtDate(new Date())}${companyName ? ` par ${escapeHtml(companyName)}` : ""} — N° ${escapeHtml(operation.id)}</footer>
</body>
</html>`;
}
